import colors from '@/theme/colors';
import { css, cx } from '@emotion/css';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper, { PopperPlacementType } from '@mui/material/Popper';
import {
  BodyMedium,
  BodyMediumEmphasized,
  BodyMediumMidEmphasis,
  BodySmallMidEmphasis,
} from '@/design-system';
import React, { useRef, useState } from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import { useGoogleTranslator } from '@/hooks';
import { languages as languagesList } from './languages';
import { useIsMobile } from '@/hooks';

interface Props {
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  placement?: PopperPlacementType;
  onClick?: () => void;
  disabled?: boolean;
}

export const LanguageDropDown: React.FC<Props> = ({
  selectedLanguage,
  setSelectedLanguage,
  placement = 'bottom-start',
  onClick,
  disabled,
}) => {
  const translateLabel = useGoogleTranslator('Translate');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [languages] = useState<{ label: string; value: string }[]>(
    Object.keys(languagesList).map(languageCode => ({
      label: languagesList[languageCode],
      value: languageCode,
    }))
  );
  const isMobile = useIsMobile();
  const LanguageLabel = isMobile ? BodySmallMidEmphasis : BodyMediumMidEmphasis;

  const handleToggle = () => {
    if (onClick) onClick();
    else {
      setOpen(prevOpen => !prevOpen);
    }
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const onSelectLanguage = (language: string) => {
    setSelectedLanguage(language);
    setOpen(false);
  };

  return (
    <div>
      <div>
        <Button
          ref={anchorRef}
          className={cx(styles.button, onClick ? styles.buttonPadding : styles.dropdownPadding)}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open}
          onClick={handleToggle}
          disabled={languages.length === 0 || disabled}
        >
          <LanguageIcon className={styles.icon} />
          <LanguageLabel
            nonTranslate
            className={styles.activeOption}
            color={colors.jungle}
          >
            {selectedLanguage === '' ? translateLabel : languagesList[selectedLanguage]}
          </LanguageLabel>
          {!onClick && <ArrowDropDownIcon className={styles.icon} />}
        </Button>
      </div>
      {!onClick && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement={placement}
          transition
          disablePortal
          className={styles.popper}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                borderRadius: '12px',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    role={undefined}
                    // eslint-disable-next-line react/jsx-no-bind
                    onKeyDown={handleListKeyDown}
                    className={styles.menuList}
                  >
                    {languages.map(({ label, value }) => (
                      <MenuItem
                        key={value}
                        className={cx(
                          styles.optionMenu,
                          selectedLanguage === value && styles.selectedOption
                        )}
                        onClick={() => onSelectLanguage(value)}
                        role="listitem"
                        tabIndex={null as unknown as number}
                      >
                        {selectedLanguage === value ? (
                          <BodyMediumEmphasized nonTranslate className={styles.optionText}>
                            {label}
                          </BodyMediumEmphasized>
                        ) : (
                          <BodyMedium nonTranslate className={styles.optionText}>
                            {label}
                          </BodyMedium>
                        )}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  );
};

const styles = {
  button: css`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    border: 1px solid ${colors.jungle} !important;
    border-radius: 8px !important;

    &:disabled {
      border-color: ${colors.stone} !important;

      span {
        color: ${colors.neutralAsh} !important;
      }

      svg {
        color: ${colors.neutralAsh};
      }
    }
  `,
  buttonPadding: css`
    padding: 4px 8px !important;
  `,
  dropdownPadding: css`
    padding: 4px 4px 4px 8px !important;
  `,
  optionMenu: css`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  `,
  optionText: css`
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  filterButton: css`
    padding: 2px 8px;
    border-radius: 10px;
    border: 1px solid ${colors.prussian400};
  `,
  selectedOption: css`
    background-color: ${colors.platinum} !important;
  `,
  activeOption: css`
    color: ${colors.jungle};
    white-space: normal;
    overflow-wrap: break-word;
  `,
  popper: css`
    max-width: 262px;
    margin-top: 6px !important;
    z-index: 9999;
  `,
  menuList: css`
    max-height: 600px;
    overflow-y: auto;
  `,
  icon: css`
    width: 20px !important;
    height: 20px !important;
    color: ${colors.jungle};
  `,
};
